import React, { useMemo, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";

import * as yup from "yup";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";

import Input from "../../../components/Input";

import { QUERY_CLIENT_SENDGRID_TEMPLATE } from "../../../config/graphql/query";
import {
  CREATE_CLIENT_SENDGRID_TEMPLATE,
  UPDATE_CLIENT_SENDGRID_TEMPLATE,
  DELETE_CLIENT_SENDGRID_TEMPLATE,
} from "../../../config/graphql/mutation";
import { EMAIL_TEMPLATES } from "../../../config/const/common";

const ClientSendGridTemplate = React.memo(() => {
  const [show, setShow] = useToggle(false);

  const { t } = useTranslation(["client", "common"]);
  const history = useHistory();

  const { id, clientId } = useParams<{ id: string; clientId: string }>();

  const schema = useMemo(
    () =>
      yup.object().shape({
        title: yup
          .string()
          .required(t("client:client.sendgrid.template.yup.title")),
        template: yup
          .string()
          .required(t("client:client.sendgrid.template.yup.template")),
        templateId: yup
          .string()
          .required(t("client:client.sendgrid.template.yup.templateid")),
      }),
    [t]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    defaultValues: {},
  });

  const { data: emailTemplateData } = useQuery(QUERY_CLIENT_SENDGRID_TEMPLATE, {
    skip: !clientId || !id,
    variables: { id, client: clientId },
    onCompleted: ({ emailTemplate }) => methods.reset(emailTemplate),
  });

  const data = emailTemplateData?.emailTemplate;

  const [onUpdate] = useMutation(UPDATE_CLIENT_SENDGRID_TEMPLATE, {
    refetchQueries: [
      {
        query: QUERY_CLIENT_SENDGRID_TEMPLATE,
        variables: { id, client: clientId },
      },
    ],
  });
  const [onCreate] = useMutation(CREATE_CLIENT_SENDGRID_TEMPLATE);
  const [onDelete] = useMutation(DELETE_CLIENT_SENDGRID_TEMPLATE);

  const onBeforeDelete = useCallback(() => {
    return onDelete({ variables: { input: { id, client: clientId } } })
      .then(() => {
        toast.success(t("client:client.information.toast.deleted"));

        history.replace(`/client/${clientId}/sendgrid-templates`);
      })
      .catch((error) => {
        toast.error(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message
        );

        history.replace(`/client/${clientId}/sendgrid-templates`);
      });
  }, [history, onDelete, clientId, id, t]);

  const onSubmit = useCallback(
    (values: IClientSendGridTemplate) => {
      if (!clientId) {
        return toast.error(t("client:client.sendgrid.error.missingclient"));
      }

      const input = {
        ...(id ? { id } : {}),
        ...(clientId ? { client: clientId } : {}),
        ...values,
      };

      if (id) {
        return onUpdate({ variables: { input } })
          .then(({ data: { updateEmailTemplate } }) => {
            methods.reset(updateEmailTemplate);
          })
          .then(() => {
            toast.success(t("client:client.information.toast.updated"));
          })
          .catch((error) => {
            toast.error(
              error?.networkError?.result?.errors?.[0]?.message ??
                error?.message
            );
          });
      }

      return onCreate({ variables: { input } })
        .then(
          ({
            data: {
              addEmailTemplate: { id },
            },
          }) => {
            toast.success(t("client:client.information.toast.created"));

            history.replace(`/client/${clientId}/sendgrid-templates/${id}`);
          }
        )
        .catch((error) => {
          toast.error(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message
          );
        });
    },
    [data]
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={"/client"}>{t("client:client.token.nav.clients")}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`/client/${clientId}`}>
              {data?.client?.title ?? "Client"}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link to={`/client/${clientId}/sendgrid-templates`}>
              {t("client:client.sendgrid.nav.sendgridtemplates")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {data?.title ?? "Title"}
          </li>
        </ol>
      </nav>
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-4">
            <div className="form-group">
              <label htmlFor="title">
                {t("client:client.sendgrid.form.title")}
              </label>
              <Input name="title" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="type">
                {t("screens:screen.screenRoute.form.template")}
              </label>
              <select
                ref={methods.register}
                name="template"
                className="custom-select"
              >
                {EMAIL_TEMPLATES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="templateId">
                {t("client:client.sendgrid.template.form.templateid")}
              </label>
              <Input name="templateId" className="form-control" />
            </div>
          </div>
          <div className="col-12">
            <input type="submit" className="btn btn-primary" />
            {clientId && (
              <button
                onClick={(e) => {
                  e.preventDefault();

                  setShow(true);
                }}
                className="btn btn-danger ml-3"
              >
                {t("common:delete")}
              </button>
            )}
          </div>
        </form>
      </FormProvider>
      <Modal
        size="sm"
        show={show}
        onHide={setShow}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.information.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.information.modal.title", {
            title: data?.client?.title,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShow(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default ClientSendGridTemplate;
